import { uploadFileApi } from "@/api/services/global/file";
import {
  createRenewVisaUploadAvatarFileDocumentApi,
  updateRenewVisaUploadAvatarFileDocumentApi,
  useGetRenewVisaUploadAvatarFileDocumentInfo,
} from "@/api/services/main/renew-visa";
import { IconCheck, IconClose } from "@/assets/icons";
import { Button } from "@/components/elements/Button";
import { LoadingArea } from "@/components/elements/Loading/Loading";
import { FILE_UPLOAD_FOR, MIME_TYPES } from "@/constants/file";
import { INVALID_PASSWORD_PROTECTED_PDF, INVALID_PDF_FORMAT } from "@/constants/invalids";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { background, divider, state } from "@/theme/colors";
import { CHECK_PDF_RESULT, RENEW_VISA_DOCUMENT_STEP_STATUS } from "@/types/enum";
import { showError } from "@/utils/error";
import { checkFileListWhetherIsActualPdf, convertKbToByte, validateRatio } from "@/utils/file";
import { toast } from "@/utils/toast";
import { Stack, styled } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useState } from "react";

type TUploadAvatarFileBtnProps = {
  status: string | undefined;
  readonly: boolean;
  renewVisaDocumentId: string;
  documentStepId: string;
};

export const UploadAvatarFileBtn: FC<TUploadAvatarFileBtnProps> = ({ status, readonly, renewVisaDocumentId, documentStepId }) => {
  const [uploading, setUploading] = useState<boolean>(false);

  const queryClient = useQueryClient();

  const { data: uploadAvatarFileData, isLoading: isLoadingUploadAvatarFile } = useGetRenewVisaUploadAvatarFileDocumentInfo({
    documentStepId,
    renewVisaDocumentId,
  });

  const handleUploadFile = async (file: File) => {
    if (file.size === 0) {
      toast.error("0Byteのファイルはアップロードできません。");
      return;
    }

    const result = await checkFileListWhetherIsActualPdf([file]);
    if (result === CHECK_PDF_RESULT.PASSWORD_PROTECTED) {
      toast.error(INVALID_PASSWORD_PROTECTED_PDF);
      return;
    }
    if (result === CHECK_PDF_RESULT.INVALID) {
      toast.error(INVALID_PDF_FORMAT);
      return;
    }

    if (file.size > convertKbToByte(10000)) {
      toast.error("ファイルサイズは10MB以内でお願いします。");
      return;
    }

    const isValidRatio = await validateRatio(file);
    if (!isValidRatio) {
      toast.error("縦横比４：３で縦長になるようにサイズ調整してください。");
      return;
    }

    if (!renewVisaDocumentId || !documentStepId) return;
    try {
      setUploading(true);
      const avatarFilePath = await uploadFileApi({ file, uploadFor: FILE_UPLOAD_FOR.EMPLOYEE_AVATAR_FILE });
      const avatarFileResizePath = await uploadFileApi({ file, uploadFor: FILE_UPLOAD_FOR.EMPLOYEE_AVATAR_FILE_RESIZE });
      const thumbnailForFormAvatarFilePath = await uploadFileApi({ file, uploadFor: FILE_UPLOAD_FOR.EMPLOYEE_AVATAR_THUMBNAIL_FOR_FORM });
      const thumbnailForListAvatarFilePath = await uploadFileApi({ file, uploadFor: FILE_UPLOAD_FOR.EMPLOYEE_AVATAR_THUMBNAIL_FOR_LIST });

      if (uploadAvatarFileData?.updater) {
        await updateRenewVisaUploadAvatarFileDocumentApi(renewVisaDocumentId, documentStepId, {
          avatarFilePath: avatarFilePath.data.filePath,
          avatarFileResizePath: avatarFileResizePath.data.filePath,
          thumbnailForFormAvatarFilePath: thumbnailForFormAvatarFilePath.data.filePath,
          thumbnailForListAvatarFilePath: thumbnailForListAvatarFilePath.data.filePath,
        });
      } else {
        await createRenewVisaUploadAvatarFileDocumentApi(renewVisaDocumentId, documentStepId, {
          avatarFilePath: avatarFilePath.data.filePath,
          avatarFileResizePath: avatarFileResizePath.data.filePath,
          thumbnailForFormAvatarFilePath: thumbnailForFormAvatarFilePath.data.filePath,
          thumbnailForListAvatarFilePath: thumbnailForListAvatarFilePath.data.filePath,
        });
      }
      toast.success("画像がアップロードされました。");
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RENEW_VISA_DOCUMENTS.FETCH_DOCUMENT_CREATION_GROUP] });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RENEW_VISA_DOCUMENTS.FETCH_APPLICATION_INFORMATION_GROUP] });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RENEW_VISA_DOCUMENTS.FETCH_APPLICATION_RESULT_INFORMATION_GROUP] });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RENEW_VISA_DOCUMENTS.FETCH_RESIDENCE_CARD_INFORMATION_GROUP] });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RENEW_VISA_DOCUMENTS.FETCH_UPLOAD_AVATAR_FILE] });
    } catch (error) {
      showError(error);
    } finally {
      setUploading(false);
    }
  };

  if (isLoadingUploadAvatarFile) return <LoadingArea />;

  return (
    <Button size="sm" variant="outline" component="label" isLoading={uploading} disabled={readonly || uploading}>
      <Stack
        mr={1}
        alignItems="center"
        justifyContent="center"
        width={16}
        height={16}
        borderRadius={16}
        bgcolor={!status ? divider.high : status === RENEW_VISA_DOCUMENT_STEP_STATUS.COMPLETE ? state.success_1 : state.warning_1}
      >
        {status && status === RENEW_VISA_DOCUMENT_STEP_STATUS.IN_PROGRESS ? (
          <IconClose color={background.white} />
        ) : (
          <IconCheck color={background.white} />
        )}
      </Stack>
      アップロード
      <VisuallyHiddenInput
        type="file"
        accept={MIME_TYPES.IMAGE.join(",")}
        onChange={(event) => event.target.files && handleUploadFile(event.target.files[0])}
        onClick={(event) => {
          event.currentTarget.value = "";
        }}
        multiple={false}
      />
    </Button>
  );
};

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});
